// Theme-aware colour helpers for the Chart.js canvases on the user site (ENG-307).
//
// Chart.js paints to <canvas>: a CSS variable never reaches it, and a
// CanvasGradient is baked the moment it is built. So every helper here returns
// a SCRIPTABLE option -- a function Chart.js re-evaluates on each update() --
// that reads the --ws-* tokens at draw time. theme.js's ws:theme sweep
// (Object.values(Chart.instances) -> update('none')) then recolours every
// registered chart, handle or no handle; the HTML legends the views rebuild in
// afterUpdate follow on their own, because they copy the RESOLVED strokeStyle.
//
// Dark must stay pixel-identical to the pre-toggle site. A token stands in for
// a literal only where the token's DARK value IS that literal: --ws-green
// #4FE0A0, --ws-red #FF7A85, --ws-ink-3 #8794A3, --ws-line-soft
// rgba(255, 255, 255, .055), --ws-green-rgb 79, 224, 160. Where a chart's old
// colour has no equal token (Chart.js's own #666 / rgba(0,0,0,0.1), the modal
// grid rgba(255, 255, 255, 0.1), the server's #16C784 / #ea3943) the caller
// passes that literal and it is kept in dark; only light reads the token.
//
// Tokens are read from <body>, not <html>: the light block (ws_theme.scss) is
// scoped to `html[data-theme="light"] body.layout-top-nav`, so on the admin
// layout they resolve to the dark values whatever the attribute says. isLight()
// mirrors that exact scope, which is why every dark-literal branch asks it and
// not a bare wsTheme.get() === 'light'.
//
// Imported by packs/application.js right after packs/base/theme.js; inline view
// scripts reach it as window.wsChart (the pack sits in <head>, not deferred).

// Dark values, straight from the ws_redesign.scss :root block, for a token that
// is not defined on the page (an rgb triplet that has not shipped yet, a page
// outside the redesign). They keep the canvas on today's colours instead of
// handing createLinearGradient an "rgba(, .28)" it would throw on.
var DARK = {
    '--ws-green': '#4FE0A0',
    '--ws-red': '#FF7A85',
    '--ws-blue': '#6C9BFF',
    '--ws-ink': '#F1F5F9',
    '--ws-ink-2': '#A5B1C0',
    '--ws-ink-3': '#8794A3',
    '--ws-line': 'rgba(255, 255, 255, .09)',
    '--ws-line-soft': 'rgba(255, 255, 255, .055)',
    '--ws-menu': '#10141C',
    '--ws-green-rgb': '79, 224, 160',
    '--ws-red-rgb': '255, 122, 133',
    '--ws-blue-rgb': '108, 155, 255'
};

// True only when the light override block actually applies to this page.
function isLight() {
    var body = document.body;
    var chosen = window.wsTheme ? window.wsTheme.get() === 'light'
                                : document.documentElement.getAttribute('data-theme') === 'light';
    return !!(chosen && body && body.classList.contains('layout-top-nav'));
}

function token(name, fallback) {
    var value = '';
    if (window.wsTheme && window.wsTheme.token) {
        value = window.wsTheme.token(name, '');
    } else if (document.body) {
        value = (getComputedStyle(document.body).getPropertyValue(name) || '').trim();
    }
    return value || fallback || DARK[name] || '';
}

// rgbToken is one of the `--ws-*-rgb` triplets ("79, 224, 160").
function rgba(rgbToken, alpha) {
    return 'rgba(' + token(rgbToken) + ', ' + alpha + ')';
}

// The token in light; the caller's dark literal otherwise. No literal given =
// the token in both themes, which is only right when its dark value is the
// colour the chart drew before (see the header).
function pick(name, dark) {
    return dark !== undefined && !isLight() ? dark : token(name);
}

// Axis colours. `dark` (optional) = { tick, grid } literals to keep in dark.
// Consumers whose font or border differ pick `.ticks.color` and `.grid.color`
// out of this rather than spreading the whole object.
export function axis(dark) {
    dark = dark || {};
    return {
        ticks: {
            color: function () { return pick('--ws-ink-3', dark.tick); },
            font: { size: 10.5 }
        },
        grid: {
            color: function () { return pick('--ws-line-soft', dark.grid); },
            drawTicks: false
        },
        border: { display: false }
    };
}

// Vertical fade under a line, rebuilt from chartArea on every evaluation so a
// theme flip re-tints it (a built CanvasGradient cannot be).
export function fade(alpha, rgbToken) {
    alpha = alpha === undefined ? 0.28 : alpha;
    rgbToken = rgbToken || '--ws-green-rgb';
    return function (context) {
        var area = context.chart.chartArea;
        if (!area) return null; // first pass, before layout
        var gradient = context.chart.ctx.createLinearGradient(0, area.top, 0, area.bottom);
        gradient.addColorStop(0, rgba(rgbToken, alpha));
        gradient.addColorStop(1, rgba(rgbToken, 0));
        return gradient;
    };
}

// `up` -> --ws-green, else --ws-red. `dark` (optional) is the literal to keep
// in dark when the old colour has no equal token: the server's trend hexes.
export function trendColor(up, dark) {
    var name = up ? '--ws-green' : '--ws-red';
    return function () { return pick(name, dark); };
}

// The server still sends color: '#16C784' (up) / '#ea3943' (down) with its
// sparkline data; this turns that into the boolean trendColor wants. Anything
// that is not the down hex counts as up, case-insensitively.
export function trendFromColor(hex) {
    return String(hex || '').toLowerCase() !== '#ea3943';
}

// Tooltip chrome. Dark keeps Chart.js's own defaults (rgba(0,0,0,0.8) card,
// white text, no border) because no token equals them; light reads the menu /
// ink / line tokens. Every tooltip option but filter/itemSort/external is
// scriptable, so these are functions too.
export function tooltip() {
    return {
        backgroundColor: function () { return isLight() ? token('--ws-menu') : 'rgba(0,0,0,0.8)'; },
        titleColor: function () { return isLight() ? token('--ws-ink') : '#fff'; },
        bodyColor: function () { return isLight() ? token('--ws-ink-2') : '#fff'; },
        borderColor: function () { return isLight() ? token('--ws-line') : 'rgba(0,0,0,0)'; },
        borderWidth: function () { return isLight() ? 1 : 0; }
    };
}

// Chartkick sparklines (boxes/_pricing_table, wax_trackers/release_dates) get
// their colour from Ruby (`colors: [...]`) and Chartkick bakes it into the
// dataset as a string, so the Chart.instances sweep cannot recolour them. Each
// sits inside a wrapper carrying data-ws-trend="up|down"; this re-reads the
// token for every chart still in the document. setOptions() rebuilds the
// options from Chartkick.options plus what it is given, so the chart's current
// options go back in with the new colour -- or `library`, `min`/`max` and
// `dataset` would be lost.
//
// The registry is the one captured when the page's charts were built, not
// whatever window.Chartkick is by the time a toggle happens. chartkick.js is a
// body <script src>, and Turbolinks re-runs every body script on each visit.
// Its UMD wrapper reassigns window.Chartkick to a fresh object (empty `charts`)
// every time it runs; only its listener / chartkick:load block is guarded. On a
// second visit to a Chartkick page the inline helpers build the charts
// synchronously during the body swap, on the object that is current then, and
// the re-run file replaces it a moment later. Read late, window.Chartkick
// would hand back an empty registry and the sparklines would keep the other
// theme's colour.
var pageCharts = null;

function capturePageCharts() {
    if (window.Chartkick && window.Chartkick.charts) pageCharts = window.Chartkick.charts;
}

function recolorChartkick() {
    var registry = pageCharts || (window.Chartkick && window.Chartkick.charts);
    if (!registry) return;
    Object.keys(registry).forEach(function (id) {
        var chart = registry[id];
        var element = chart.getElement && chart.getElement();
        var wrap = element && element.isConnected && element.closest('[data-ws-trend]');
        if (!wrap) return;
        var up = wrap.getAttribute('data-ws-trend') === 'up';
        var color = token(up ? '--ws-green' : '--ws-red');
        var options = chart.getOptions();
        // Both first-paint hooks below fire on a hard load; a chart already on
        // this colour is left alone rather than redrawn a second time.
        if ((options.colors || [])[0] === color) return;
        chart.setOptions(Object.assign({}, options, { colors: [color] }));
    });
}

document.addEventListener('ws:theme', recolorChartkick);

// The charts come into existence at two different moments, hence two hooks;
// each one captures the registry (above) and, in light, recolours on first
// paint, because Ruby only knows the dark hex. chartkick.js dispatches
// chartkick:load on window from its own setTimeout(0), only the first time it
// runs in the document, and the helper's createChart listeners build every
// chart during that dispatch. That is the hook for a hard load and for the
// first Turbolinks visit of the session to a Chartkick page, where the file is
// still loading when turbolinks:load fires. On later visits Chartkick is
// already present and the inline helpers build the charts synchronously during
// the body swap; turbolinks:load fires in that same task, before the re-run
// file can swap window.Chartkick, so it is the hook there. Either way a
// setTimeout(0) queued from the event lands after the construction. The
// isConnected test skips charts left over from earlier pages. Dark skips the
// recolour: the baked hex already is the token's dark value.
function onChartsBuilt() {
    capturePageCharts();
    if (isLight()) setTimeout(recolorChartkick, 0);
}
window.addEventListener('chartkick:load', onChartsBuilt);
document.addEventListener('turbolinks:load', onChartsBuilt);

export { isLight, token, rgba };

window.wsChart = {
    axis: axis,
    fade: fade,
    trendColor: trendColor,
    tooltip: tooltip,
    trendFromColor: trendFromColor,
    isLight: isLight,
    token: token,
    rgba: rgba
};
